import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PE_IMG, Vine, PEHead, GoldRule } from "./bits";
import Reveal from "../Reveal";

const STEPS = [
  { n: "01", name: "Discover", desc: "Tell us what you're looking for — hair, skin, makeup, or all of it." },
  { n: "02", name: "Customize", desc: "Your appointment is designed around you, never pre-set." },
  { n: "03", name: "Experience", desc: "Unhurried, polished, and entirely yours." },
  { n: "04", name: "Maintain", desc: "Leave knowing exactly how to keep the feeling." },
  { n: "05", name: "Return", desc: "Beauty works best as a relationship, not an appointment." },
];

function PEJourney() {
  return (
    <section id="pe-journey" data-testid="pe-journey" className="relative bg-pe-cream py-24 md:py-32 overflow-hidden">
      <Vine className="absolute -right-6 top-10 h-[85%] opacity-50 hidden lg:block" flip />
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <PEHead overline="The Prismatic Journey" title={<>An ongoing relationship <span className="italic">with your beauty.</span></>} />
        <div className="relative pl-12 md:pl-16">
          <motion.div
            aria-hidden="true"
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 2, ease: "easeOut" }}
            className="absolute left-3 md:left-5 top-2 bottom-2 w-px bg-pe-gold/70 origin-top"
          />
          <div className="space-y-12">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="relative" data-testid={`pe-journey-step-${s.n}`}>
                  <span className="absolute -left-12 md:-left-16 top-1 w-7 h-7 md:w-9 md:h-9 rounded-full border border-pe-gold/70 bg-pe-ivory flex items-center justify-center font-sans text-[9px] tracking-[0.1em] text-pe-brass">
                    {s.n}
                  </span>
                  <h3 className="font-pe text-2xl md:text-3xl text-pe-forest mb-1.5">{s.name}</h3>
                  <p className="font-sans font-light text-sm md:text-base text-pe-body">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PEScripture() {
  return (
    <section data-testid="pe-scripture" className="bg-pe-ivory border-y border-pe-gold/15 py-20 md:py-24">
      <Reveal className="max-w-2xl mx-auto px-5 text-center">
        <p className="font-sans text-[10px] tracking-[0.35em] uppercase text-pe-olive mb-8">A Quiet Note</p>
        <blockquote>
          <p className="font-pe italic text-2xl md:text-3xl leading-relaxed text-pe-forest">
            "She is clothed with strength and dignity; she can laugh at the days to come."
          </p>
          <cite className="not-italic font-sans text-[10px] tracking-[0.3em] uppercase text-pe-brass mt-6 block">— Proverbs 31:25</cite>
        </blockquote>
      </Reveal>
    </section>
  );
}

function PEAbout() {
  return (
    <section id="pe-about" data-testid="pe-about" className="bg-pe-ivory py-24 md:py-32 overflow-hidden">
      <div className="max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="relative max-w-md mx-auto order-last lg:order-first">
            <div className="absolute -inset-3 border border-pe-gold/40 rounded-sm -translate-x-4 translate-y-4" aria-hidden="true" />
            <img
              src="/images/shanelle-stairs.jpg"
              alt="Shanelle — the founder of Prismatic Essence"
              data-testid="pe-about-image"
              className="relative w-full aspect-[4/3] object-cover object-[50%_35%] rounded-sm"
              loading="lazy"
            />
            <p className="mt-5 font-pe italic text-lg text-pe-olive text-center">shanelle — the hands behind the house</p>
          </div>
        </Reveal>
        <div>
          <PEHead center={false} overline="About Prismatic Essence" title={<>Beauty in Every <span className="italic">Dimension.</span></>} />
          <Reveal>
            <div className="-mt-6 space-y-5 font-sans font-light text-pe-body leading-relaxed text-base md:text-lg">
              <p>
                Prismatic Essence was born from something simple: the feeling of luxury that came from taking the
                time to care for myself. As a young girl, beauty was more than getting something done — it was
                confidence, self-expression, and feeling good about who I was. That feeling stayed with me.
              </p>
              <p>
                As I grew, I became licensed and certified so I could create that same feeling for other women and
                girls — not simply by providing beauty services, but by creating an experience they genuinely enjoy.
              </p>
              <p>
                Beauty has more than one dimension. It can be hair. It can be skin. It can be makeup, maintenance,
                learning, self-expression — or simply taking a moment to feel good about yourself.
              </p>
              <p className="font-pe italic text-xl text-pe-forest">
                Every service is an opportunity to help you feel confident, cared for, and beautiful in your own dimension.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function PEBooking({ onBook }) {
  return (
    <section id="pe-book" data-testid="pe-book" className="relative bg-pe-forest text-pe-ivory py-28 md:py-36 overflow-hidden">
      <img src={PE_IMG.hero} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-15" loading="lazy" />
      <div className="absolute inset-0 bg-pe-forest/70" aria-hidden="true" />
      <div className="relative max-w-2xl mx-auto px-5 text-center">
        <PEHead light overline="Reservations" title={<>Book your <span className="italic">service.</span></>} />
        <Reveal>
          <p className="font-sans font-light text-pe-ivory/80 leading-relaxed mb-10 -mt-4">
            Private appointments. Personal attention. One guest at a time. A $25 deposit secures your time and is applied to your service.
          </p>
          <button
            data-testid="pe-book-button"
            onClick={() => onBook()}
            className="rounded-full border border-pe-gold bg-pe-gold/10 text-pe-ivory px-10 py-4 font-sans text-[11px] tracking-[0.3em] uppercase hover:bg-pe-gold hover:text-pe-forest transition-colors"
          >
            Book Your Service
          </button>
        </Reveal>
      </div>
    </section>
  );
}

function PEFooter() {
  return (
    <footer data-testid="pe-footer" className="bg-pe-forest text-pe-ivory border-t border-pe-gold/20">
      <div className="bg-pe-beige/15 border-t border-pe-gold/20">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-pe-ivory/60 text-center sm:text-left">
            Part of the Eclectically Nelle ecosystem
          </p>
          <Link
            to="/"
            data-testid="back-to-parent-link"
            className="font-sans text-[10px] tracking-[0.25em] uppercase text-pe-gold hover:text-pe-ivory transition-colors shrink-0"
          >
            ← Back to Eclectically Nelle
          </Link>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 flex flex-col items-center text-center gap-5">
        <img src="/images/pe-logo.png" alt="Prismatic Essence monogram" className="h-14 w-auto" />
        <p className="font-pe tracking-[0.3em] text-lg">PRISMATIC ESSENCE</p>
        <p className="font-pe italic text-pe-sage">Beauty in Every Dimension.</p>
        <GoldRule className="w-20" center />
        <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-pe-ivory/40">© 2026 Prismatic Essence · Part of the Eclectically Nelle ecosystem</p>
      </div>
    </footer>
  );
}

export default function PEClosing({ onBook }) {
  return (
    <>
      <PEJourney />
      <PEScripture />
      <PEAbout />
      <PEBooking onBook={onBook} />
      <PEFooter />
    </>
  );
}
